import { supabase } from "@/lib/supabase";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

const STORAGE_KEY = "notification-storage";

let activeSubscription = null;
let isSubscribing = false;

export const useNotificationStore = create(
  persist(
    (set, get) => ({
      notifications: [],
      isLoading: false,
      initialized: false,
      error: null,
      unreadCount: 0,
      activeTab: "all",
      lastFetchedAt: null,

      setInitialized: (value) => set({ initialized: value }),
      setIsLoading: (value) => set({ isLoading: value }),

      fetchNotifications: async (userId) => {
        if (!userId) return;

        try {
          set({ isLoading: true });

          const { data, error } = await supabase
            .from("notifications")
            .select("*")
            .eq("user_id", userId)
            .order("created_at", { ascending: false })
            .limit(50);

          if (error) throw error;

          const unreadCount = data.filter(
            (notification) => !notification.is_read
          ).length;

          set({
            notifications: data || [],
            unreadCount,
            isLoading: false,
            lastFetchedAt: new Date().toISOString(),
          });

          return { success: true, data };
        } catch (error) {
          console.error("Error fetching notifications:", error);
          set({ error: error.message, isLoading: false });
          return { success: false, error: error.message };
        }
      },

      initializeNotifications: async (userId) => {
        if (!userId) return;

        try {
          set({ isLoading: true });

          const result = await get().fetchNotifications(userId);

          if (!result.success) throw new Error(result.error);

          set({
            initialized: true,
            isLoading: false,
          });

          get().subscribeToNotifications(userId);

          return { success: true };
        } catch (error) {
          console.error("Error initializing notifications:", error);
          set({ error: error.message, isLoading: false });
          return { success: false, error: error.message };
        }
      },

      subscribeToNotifications: (userId) => {
        if (!userId || isSubscribing || activeSubscription)
          return activeSubscription;

        const subscription = setupRealtimeSubscription(userId);
        return subscription;
      },

      unsubscribeFromNotifications: () => {
        if (activeSubscription) {
          try {
            if (typeof activeSubscription.unsubscribe === "function") {
              activeSubscription.unsubscribe();
            } else if (
              supabase &&
              typeof supabase.removeChannel === "function"
            ) {
              supabase.removeChannel(activeSubscription);
            }
          } catch (error) {
            console.debug("Notification unsubscribe completed:", error.message);
          }
          activeSubscription = null;
          isSubscribing = false;
          console.log("Unsubscribed from notification updates");
        }
      },

      markAllAsRead: async (userId) => {
        if (!userId) return;

        try {
          set({ isLoading: true });

          const { error } = await supabase
            .from("notifications")
            .update({ is_read: true, updated_at: new Date().toISOString() })
            .eq("user_id", userId)
            .eq("is_read", false);

          if (error) throw error;

          set((state) => ({
            notifications: state.notifications.map((notification) => ({
              ...notification,
              is_read: true,
            })),
            unreadCount: 0,
            isLoading: false,
          }));

          return { success: true };
        } catch (error) {
          console.error("Error marking notifications as read:", error);
          set({ error: error.message, isLoading: false });
          return { success: false, error: error.message };
        }
      },

      markAsRead: async (notificationId) => {
        try {
          const { error } = await supabase
            .from("notifications")
            .update({ is_read: true, updated_at: new Date().toISOString() })
            .eq("id", notificationId);

          if (error) throw error;

          set((state) => {
            const updatedNotifications = state.notifications.map(
              (notification) =>
                notification.id === notificationId
                  ? { ...notification, is_read: true }
                  : notification
            );

            return {
              notifications: updatedNotifications,
              unreadCount: updatedNotifications.filter((n) => !n.is_read)
                .length,
            };
          });

          return { success: true };
        } catch (error) {
          console.error("Error marking notification as read:", error);
          set({ error: error.message });
          return { success: false, error: error.message };
        }
      },

      createNotification: async (notification) => {
        try {
          const { data, error } = await supabase
            .from("notifications")
            .insert([notification])
            .select();

          if (error) throw error;

          if (data && data.length > 0) {
            set((state) => ({
              notifications: [data[0], ...state.notifications],
              unreadCount: state.unreadCount + 1,
            }));
          }

          return { success: true, data: data[0] };
        } catch (error) {
          console.error("Error creating notification:", error);
          return { success: false, error: error.message };
        }
      },

      deleteNotification: async (notificationId) => {
        try {
          const { error } = await supabase
            .from("notifications")
            .delete()
            .eq("id", notificationId);

          if (error) throw error;

          set((state) => {
            const wasUnread = state.notifications.find(
              (n) => n.id === notificationId && !n.is_read
            );

            return {
              notifications: state.notifications.filter(
                (n) => n.id !== notificationId
              ),
              unreadCount: wasUnread
                ? state.unreadCount - 1
                : state.unreadCount,
            };
          });

          return { success: true };
        } catch (error) {
          console.error("Error deleting notification:", error);
          return { success: false, error: error.message };
        }
      },

      setActiveTab: (tab) => set({ activeTab: tab }),

      getFilteredNotifications: () => {
        const { notifications, activeTab } = get();

        if (activeTab === "all") {
          return notifications;
        }

        return notifications.filter(
          (notification) => notification.type === activeTab
        );
      },

      checkForNewNotifications: async (userId) => {
        if (!userId || !get().lastFetchedAt) return;

        try {
          const { data, error } = await supabase
            .from("notifications")
            .select("*")
            .eq("user_id", userId)
            .gt("created_at", get().lastFetchedAt)
            .order("created_at", { ascending: false });

          if (error) throw error;

          if (data && data.length > 0) {
            set((state) => {
              const existingIds = new Set(state.notifications.map((n) => n.id));
              const newNotifications = data.filter(
                (n) => !existingIds.has(n.id)
              );

              const newUnreadCount =
                state.unreadCount +
                newNotifications.filter((n) => !n.is_read).length;

              return {
                notifications: [...newNotifications, ...state.notifications],
                unreadCount: newUnreadCount,
                lastFetchedAt: new Date().toISOString(),
              };
            });
          }
        } catch (error) {
          console.error("Error checking for new notifications:", error);
        }
      },

      resetStore: () => {
        if (activeSubscription) {
          try {
            if (typeof activeSubscription.unsubscribe === "function") {
              activeSubscription.unsubscribe();
            } else if (
              supabase &&
              typeof supabase.removeChannel === "function"
            ) {
              supabase.removeChannel(activeSubscription);
            }
          } catch (error) {
            console.debug(
              "Notification store cleanup completed:",
              error.message
            );
          }
          activeSubscription = null;
          isSubscribing = false;
        }

        set({
          notifications: [],
          isLoading: false,
          initialized: false,
          error: null,
          unreadCount: 0,
          activeTab: "all",
          lastFetchedAt: null,
        });
      },
    }),
    {
      name: STORAGE_KEY,
      storage:
        typeof window !== "undefined"
          ? createJSONStorage(() => localStorage)
          : undefined,
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
        initialized: state.initialized,
        activeTab: state.activeTab,
        lastFetchedAt: state.lastFetchedAt,
      }),
    }
  )
);

const setupRealtimeSubscription = (userId) => {
  if (!userId || isSubscribing || activeSubscription) return activeSubscription;

  isSubscribing = true;

  if (activeSubscription) {
    try {
      if (typeof activeSubscription.unsubscribe === "function") {
        activeSubscription.unsubscribe();
      } else if (supabase && typeof supabase.removeChannel === "function") {
        supabase.removeChannel(activeSubscription);
      }
    } catch (error) {
      console.debug("Previous subscription cleanup completed:", error.message);
    }
    activeSubscription = null;
  }

  try {
    const channelName = `notifications-${userId}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    console.debug(`Setting up notification store subscription: ${channelName}`);

    activeSubscription = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (!useNotificationStore) return;

          const store = useNotificationStore.getState();
          if (!store) return;

          try {
            if (payload.eventType === "INSERT") {
              useNotificationStore.setState({
                notifications: [payload.new, ...store.notifications],
                unreadCount: store.unreadCount + (payload.new.is_read ? 0 : 1),
              });
            } else if (payload.eventType === "UPDATE") {
              const wasRead = store.notifications.find(
                (n) => n.id === payload.new.id
              )?.is_read;

              const becameRead =
                wasRead !== payload.new.is_read && payload.new.is_read;

              useNotificationStore.setState({
                notifications: store.notifications.map((notification) =>
                  notification.id === payload.new.id
                    ? payload.new
                    : notification
                ),
                unreadCount: becameRead
                  ? store.unreadCount - 1
                  : store.unreadCount,
              });
            } else if (payload.eventType === "DELETE") {
              const wasUnread = store.notifications.find(
                (n) => n.id === payload.old.id && !n.is_read
              );

              useNotificationStore.setState({
                notifications: store.notifications.filter(
                  (n) => n.id !== payload.old.id
                ),
                unreadCount: wasUnread
                  ? store.unreadCount - 1
                  : store.unreadCount,
              });
            }
          } catch (err) {
            console.error("Error handling notification update:", err);
          }
        }
      )
      .subscribe((status) => {
        console.debug(
          `Notification store subscription status for ${channelName}: ${status}`
        );
        if (status === "SUBSCRIBED") {
          console.debug(
            `Notification store subscription established: ${channelName}`
          );
        } else if (status === "CHANNEL_ERROR") {
          // CHANNEL_ERROR can occur when connection closes, which is normal
          // Only log as warning, not error, to reduce noise in console
          console.warn(
            `Notification store subscription connection closed for channel ${channelName}. This is normal and may reconnect automatically.`
          );
        } else if (status === "TIMED_OUT") {
          // Timeout is also somewhat expected in long-running connections
          console.warn(
            `Notification store subscription timed out for channel ${channelName}. Connection may reconnect automatically.`
          );
        } else if (status === "CLOSED") {
          console.debug(
            `Notification store subscription closed: ${channelName}`
          );
        }
      });

    return activeSubscription;
  } catch (err) {
    console.error("Error setting up notification subscription:", err);
    return null;
  } finally {
    isSubscribing = false;
  }
};

export default useNotificationStore;
